import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2 } from 'lucide-react';
import formatPhone from '../../../utils/formatPhoneForUser';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import { cn } from '@/lib/utils';
import CustomerAvatar from '@/components/customers/CustomerAvatar';
import { goToCustomerConversation } from '@/utils/goToCustomerConversation';

/**
 * The customer linked to a message, attached to it like a contact card.
 * Clicking it opens the customer's conversation.
 */
const CustomerCard = ({ customer }) => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const maskPhone = usePhoneMask();

	if (!customer) {
		return null;
	}

	const contact = customer.phone
		? maskPhone(formatPhone(customer.phone))
		: customer.email;

	const handleGoToConversation = async () => {
		if (!customer?._id || loading) return;

		setLoading(true);
		try {
			await goToCustomerConversation({
				customerId: customer._id,
				navigate,
			});
		} catch {
			// Error already handled by utility
		} finally {
			setLoading(false);
		}
	};

	return (
		<button
			type="button"
			onClick={handleGoToConversation}
			title="Open their conversation"
			className={cn(
				'flex w-64 min-w-0 max-w-full items-center gap-3 rounded-[18px] border border-border/70 bg-background px-3 py-2.5 text-left shadow-[0_1px_2px_rgba(0,0,0,.04)] transition-colors',
				'hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-stone-900'
			)}
		>
			<CustomerAvatar
				customer={customer}
				className="size-9 shrink-0"
				fallbackClassName="text-sm font-medium"
			/>
			<span className="flex min-w-0 flex-1 flex-col">
				<span className="truncate text-[15px] font-medium leading-5">
					{maskPhone(customer.name) || 'Customer'}
				</span>
				<span className="truncate text-xs leading-4 text-muted-foreground">
					{contact ? `Customer · ${contact}` : 'Customer'}
				</span>
			</span>
			{loading ? (
				<Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
			) : (
				<ChevronRight className="size-4 shrink-0 text-muted-foreground" />
			)}
		</button>
	);
};

export default CustomerCard;
